import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import Intake from "./pages/Intake";
import Layout from "./components/Layout";
import BouquetHome from "./pages/BouquetHome";
import Growing from "./pages/Growing";
import Pods from "./pages/Pods";
import PodDetail from "./pages/PodDetail";
import Moments from "./pages/Moments";
import Community from "./pages/Community";
import MemberProfile from "./pages/MemberProfile";
import JoinPod from "./pages/JoinPod";
import Features from "./pages/Features";
import Profile from "./pages/Profile";
import Admin from "./pages/Admin";
import Pricing from "./pages/Pricing";
import PaymentResult from "./pages/PaymentResult";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding_status === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="pending-approval-screen">
        <div className="max-w-md text-center space-y-4">
          <img src="/logo.png" alt="Bouquet" className="h-20 w-44 object-cover object-[center_62%] rounded-lg mx-auto" />
          <h1 className="font-display text-3xl font-medium tracking-tight">You're on the list</h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Bouquet is a small, trust-based community. Your request is being reviewed — you'll be welcomed in
            as soon as it's approved.
          </p>
          <p className="text-xs text-muted-foreground">Signed in as {user.email}</p>
        </div>
      </div>
    );
  }
  if (!user.intake_completed) return <Navigate to="/onboarding" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/join/:podId" element={<JoinPod />} />
      <Route path="/features" element={<Features />} />
      <Route path="/onboarding" element={<OnboardingGuard />} />
      <Route path="/" element={<Protected><Layout /></Protected>}>
        <Route index element={<BouquetHome />} />
        <Route path="growing" element={<Growing />} />
        <Route path="pods" element={<Pods />} />
        <Route path="pods/:podId" element={<PodDetail />} />
        <Route path="moments" element={<Moments />} />
        <Route path="community" element={<Community />} />
        <Route path="members/:userId" element={<MemberProfile />} />
        <Route path="profile" element={<Profile />} />
        <Route path="admin" element={<Admin />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="payment/success" element={<PaymentResult />} />
        <Route path="payment/cancel" element={<PaymentResult canceled />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function OnboardingGuard() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Intake />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
